import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getTeacherClassroomProjects } from '../../../store/thunks/user-teacher.thunk';
import {
  setActiveClassroomProject,
  setSelectProjectById,
  setShowAddNewProject,
  setShowPublishingTimelinePopup,
  setShowStartTourPopup,
} from '../../../store/userSlice';
import {
  DashboardProjectModel,
  ProjectModel,
  ProjectTimelineStep,
  StudentModel,
  TimelineStepId,
} from '../../../interfaces/project.interface';
import { getClassroomProjectStudents } from '../services/Student.service';
import ProjectSelector from '../components/ProjectSelector';
import Modal from '../../../components/Common/modal/Modal';
import { convertTimestampToDateTimeString, isOdd, toStartOfLocalDay } from '../../../helper/helper';
import { MetricCard, TeacherTimelineStepSourceURL, TimelineStep } from '../components/dashboard/dashboard.model';
import {
  BookIcon,
  BoxIcon,
  BoxIconDone,
  CircularArrowIcon,
  CircularArrowIconDone,
  ConfettiIcon,
  ConfettiIconDone,
  DocumentArrowIcon,
  DocumentArrowIconDone,
  DocumentIcon,
  EditIcon,
  EnvelopeIcon,
  EnvelopeIconDone,
  ErrorAlertRedIcon,
  HourglassIcon,
  HourglassIconDone,
  InfoIcon,
  PersonIcon,
  PersonIconDone,
  PrinterIcon,
  PrinterIconDone,
  RocketIcon,
  RocketTimelineIcon,
  RocketTimelineIconDone,
  ShoppingBagIcon,
} from '../components/dashboard/dashboard-images';
import { FirebaseTimestamp } from '../../../interfaces/_common.interface';
import { Link, useNavigate } from 'react-router-dom';
import ResourceGallery from '../components/dashboard/ResourceGallery';
import {
  getProjectTemplateResources,
  WpMediaResource,
} from '../services/ProjectTemplate.service';

const TeacherClassroomDashboard = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const classrooms = useSelector((state: RootState) => state.user.classrooms);
  const activeClassroom = useSelector((state: RootState) => state.user.activeClassroom);
  const activeClassroomProjects = useSelector((state: RootState) => state.user.activeClassroomProjects);
  const activeClassroomProject = useSelector((state: RootState) => state.user.activeClassroomProject);
  const selectProjectById = useSelector((state: RootState) => state.user.selectProjectById);
  const teacherTourState = useSelector((state: RootState) => state.user.teacherTour);
  const [selectedProject, setSelectedProject] = useState<ProjectModel | null>(null);
  const [students, setStudents] = useState<StudentModel[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardProjectModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTimelineDetails, setShowTimelineDetails] = useState<TimelineStep | null>(null);
  const [nextTimelineStep, setNextTimelineStep] = useState<TimelineStep | null>(null);
  const [publishingKitResources, setPublishingKitResources] = useState<WpMediaResource[]>([]);
  const [publishingKitResourcesLoading, setPublishingKitResourcesLoading] = useState(false);
  const [publishingKitResourcesFetched, setPublishingKitResourcesFetched] = useState(false);
  const [publishingKitResourcesError, setPublishingKitResourcesError] = useState(false);
  const phoneNumber = '+447767309948';

  useEffect(() => {
    if (activeClassroom?.id) {
      dispatch(getTeacherClassroomProjects(activeClassroom.id));
    }
  }, [activeClassroom?.id, dispatch]);

  useEffect(() => {
    if (activeClassroomProjects && activeClassroomProjects.length > 0) {
      const defaultProject = activeClassroomProjects.find((p) => p.isDefaultProject) || activeClassroomProjects[0];
      setSelectedProject(defaultProject);
      dispatch(setActiveClassroomProject({ project: defaultProject }));
    } else if (activeClassroomProjects && activeClassroomProjects.length === 0) {
      setLoading(false);
    }
  }, [activeClassroomProjects, dispatch]);

  // Fetch students when project changes
  useEffect(() => {
    if (activeClassroom?.id && selectedProject?.id && selectedProject.classroomId === activeClassroom?.id) {
      fetchDashboardData();
    }
  }, [activeClassroom?.id, selectedProject?.id]);

  // Fetch WP resources when the Publishing Kit details modal is opened
  useEffect(() => {
    if (
      showTimelineDetails?.id === 'publishing-kit' &&
      selectedProject?.projectTemplateId &&
      !publishingKitResourcesFetched
    ) {
      setPublishingKitResourcesLoading(true);
      setPublishingKitResourcesError(false);
      getProjectTemplateResources(selectedProject.projectTemplateId)
        .then((resources) => {
          setPublishingKitResources(resources);
          setPublishingKitResourcesFetched(true);
        })
        .catch(() => {
          setPublishingKitResourcesError(true);
        })
        .finally(() => setPublishingKitResourcesLoading(false));
    }
  }, [showTimelineDetails?.id, selectedProject?.projectTemplateId, publishingKitResourcesFetched]);

  // Clear resource cache when project changes
  useEffect(() => {
    setPublishingKitResources([]);
    setPublishingKitResourcesFetched(false);
    setPublishingKitResourcesError(false);
  }, [selectedProject?.id]);

  const fetchDashboardData = async () => {
    if (!activeClassroom?.id || !selectedProject?.id) return;

    setLoading(true);
    try {
      const studentsList = await getClassroomProjectStudents(activeClassroom.id, selectedProject.id, true);
      if (studentsList && studentsList.students) {
        setStudents(studentsList.students);
      }
      if (studentsList && studentsList.stats) {
        setDashboardStats(studentsList.stats);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const mapDateToLocalString = (date: FirebaseTimestamp | Date | null | undefined) => {
    if (!date) return 'Date not available yet';
    const localDate = new Date(convertTimestampToDateTimeString(date));
    console.log(localDate);
    return localDate.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  };

  // Calculate real metrics from student data
  const calculateMetrics = (timeline: TimelineStep[]): MetricCard[] => {
    if (!activeClassroomProject) {
      return [];
    }
    const totalStudents = students?.length || 0;

    const startDate = selectedProject?.startDate ? mapDateToLocalString(selectedProject?.startDate) : null; // Default date from image
    const timelineStepsValueMap = (selectedProject?.timelineSteps || []).reduce(
      (acc: Map<TimelineStepId, ProjectTimelineStep>, step: ProjectTimelineStep) => {
        acc.set(step.id, step);
        return acc;
      },
      new Map<TimelineStepId, ProjectTimelineStep>(),
    );

    // const editStartDateMode = !startDate || new Date(startDate).getTime() > new Date().getTime();
    const editStartDateMode = true;

    return [
      {
        id: 'start-date',
        title: 'Change Start Date',
        icon: <RocketIcon />,
        value: selectedProject?.startDateMode === 'month' ? '-' : startDate || '',
        isEditable: editStartDateMode,
        onClick: () => {
          editStartDateMode &&
            activeClassroomProject &&
            dispatch(setShowPublishingTimelinePopup({ projectId: activeClassroomProject.id }));
        },
        status:
          selectedProject?.startDateMode !== 'month' &&
          ((startDate && new Date(startDate).getTime() > new Date().getTime()) ||
            (timeline?.slice(0, 4)?.every((step) => step.isCompleted) &&
              timelineStepsValueMap.get(TimelineStepId.START_DATE_WITH_CLASS)?.isCompleted))
            ? 'ok'
            : 'error',
        type: editStartDateMode ? 'editable' : 'static',
      },
      {
        id: 'students-started',
        title: 'Students Started',
        icon: <BookIcon />,
        value: `${dashboardStats?.bookCreatedStudents || 0}/${totalStudents}`,
        type: 'static',
      },
      {
        id: 'parent-responses',
        title: 'Parent Responses',
        icon: <DocumentIcon />,
        value: `${dashboardStats?.parentResponses || 0}/${totalStudents}`,
        type: 'static',
      },
      {
        id: 'orders-received',
        title: 'Order Received',
        icon: <ShoppingBagIcon />,
        value: `${dashboardStats?.studentsWithOrders || 0}/${totalStudents}`,
        type: 'static',
      },
    ];
  };

  // Calculate timeline steps based on project and student data
  const calculateTimelineSteps = (): TimelineStep[] => {
    const totalStudents = students.length || 0;

    const startDate = selectedProject?.startDate ? mapDateToLocalString(selectedProject?.startDate) : null; // Default date from image

    const timelineStepsValueMap = (selectedProject?.timelineSteps || []).reduce(
      (acc: Map<TimelineStepId, ProjectTimelineStep>, step: ProjectTimelineStep) => {
        acc.set(step.id, step);
        return acc;
      },
      new Map<TimelineStepId, ProjectTimelineStep>(),
    );

    const startDateValue = startDate && toStartOfLocalDay(new Date(convertTimestampToDateTimeString(startDate)));
    const currentDate = toStartOfLocalDay(new Date());

    const isStartDateCompleted = (startDateValue && currentDate?.getTime() > startDateValue?.getTime()) || false;

    const makeLink = (url: TeacherTimelineStepSourceURL, title: string) => {
      return (
        <Link
          to={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondaryStoryAdditionalLightBlueText hover:underline"
        >
          {title}
        </Link>
      );
    };

    const steps: TimelineStep[] = [
      {
        id: 'publishing-kit',
        title: 'Publishing Kit Claimed',
        icon: <EnvelopeIcon />,
        completedIcon: <EnvelopeIconDone />,
        isCompleted: timelineStepsValueMap.get(TimelineStepId.PUBLISHING_KIT_CLAIMED)?.isCompleted || false,
        size: 'small',
        details: selectedProject?.projectTemplateId ? (
          <div>
            <ResourceGallery
              resources={publishingKitResources}
              loading={publishingKitResourcesLoading}
              error={publishingKitResourcesError}
              onRetry={() => setPublishingKitResourcesFetched(false)}
            />
          </div>
        ) : (
          <p>
            You’re all set to begin. Your publishing kit includes printable flyers and optional writing worksheets.
            You’ll find everything you need on the{' '}
            {makeLink(TeacherTimelineStepSourceURL.PublishingKitClaimed, 'Publishing Kit Downloads')} page.
          </p>
        ),
      },
      {
        id: 'add-students',
        title: 'Add Students',
        icon: <PersonIcon />,
        completedIcon: <PersonIconDone />,
        isCompleted: totalStudents > 0,
        size: 'small',
        details: (
          <>
            <p>
              Add all your students in the Students tab of your portal. This automatically generates their login details
              and ensures their books are saved to your classroom library. You can add them manually or import directly
              from Clever.
            </p>
            <p>View the {makeLink(TeacherTimelineStepSourceURL.TeacherGuide, 'teacher guide.')}</p>
          </>
        ),
        taskIcon: '👥',
        taskName: 'Add your students',
        taskDescription: 'You can add students now and update the list anytime.',
        taskButtonText: 'Continue',
        taskClick: () => navigateTo('/classroom/students?addStudents=true'),
      },
      {
        id: 'send-flyers',
        title: 'Send Home Pre-Session Flyers',
        icon: <DocumentArrowIcon />,
        completedIcon: <DocumentArrowIconDone />,
        isCompleted: timelineStepsValueMap.get(TimelineStepId.SEND_PRE_SESSION_FLYERS)?.isCompleted || false,
        size: 'medium',
        details: (
          <>
            <p>
              Print and send these flyers home before students start writing. They include required parent consent (for
              digital publishing), a book preview link, and a 10% discount valid for 4 days. You can also email parents
              directly using our template.
            </p>
            <p>
              Use the {makeLink(TeacherTimelineStepSourceURL.EmailTemplatePreSessionFlyers, 'email template')} and view
              the {makeLink(TeacherTimelineStepSourceURL.TeacherGuideFlyers, 'teacher guide.')}
            </p>
          </>
        ),
        taskIcon: '📨',
        taskName: 'Send flyers home',
        taskDescription: 'Families will need these before your start date.',
        taskButtonText: 'Continue',
        taskClick: () => navigateTo('/classroom/students'),
      },
      {
        id: 'print-logins',
        title: 'Print Student Logins (If relevant)',
        icon: <PrinterIcon />,
        completedIcon: <PrinterIconDone />,
        isCompleted: timelineStepsValueMap.get(TimelineStepId.PRINT_STUDENT_LOGINS)?.isCompleted || false,
        size: 'medium',
        details: (
          <>
            <p>
              You can either print the login details we generate or have students sign in with Clever. All accounts are
              ready once students are added. The login details are useful for home access, even if using Clever.
            </p>
            <p>View the {makeLink(TeacherTimelineStepSourceURL.TeacherGuidePasswords, 'teacher guide.')}</p>
          </>
        ),
        taskIcon: '🔑',
        taskName: 'Print student logins',
        taskDescription: 'Have logins ready for Day One.',
        taskButtonText: 'Continue',
        taskClick: () => navigateTo('/classroom/students'),
      },
      {
        id: 'start-date',
        title: 'Start Date with Class',
        icon: <RocketTimelineIcon />,
        completedIcon: <RocketTimelineIconDone />,
        error: selectedProject?.startDateMode === 'month' || !startDate ? 'Start date is required' : null,
        isCompleted: isStartDateCompleted || false,
        date: selectedProject?.startDateMode === 'month' ? '-' : startDate || '-',
        size: 'small',
        details: (
          <>
            <p>
              This is your official project kickoff. On this date, students can either begin by brainstorming on
              printed writing sheets or dive straight into the platform if they are expanding on an idea.
            </p>
            <p>Download the {makeLink(TeacherTimelineStepSourceURL.WritingSheets, 'writing sheets')}</p>
          </>
        ),
        taskIcon: !startDate ? '📅' : '⏳',
        taskName: !startDate ? 'Pick your start date' : `You’re all set until ${startDate}`,
        taskDescription: !startDate
          ? 'Choose when your class begins so we can guide you.'
          : 'In the meantime, explore writing sheets and lesson plans you would like to use.',
        taskButtonText: !startDate ? 'Set Date' : 'Explore',
        taskClick: () =>
          !startDate
            ? activeClassroomProject &&
              dispatch(setShowPublishingTimelinePopup({ projectId: activeClassroomProject.id }))
            : window.open(TeacherTimelineStepSourceURL.PublishingKitClaimed, '_blank', 'noopener,noreferrer'),
      },
      {
        id: 'students-login',
        title: 'Students Login to Build My Story',
        icon: <CircularArrowIcon />,
        completedIcon: <CircularArrowIconDone />,
        isCompleted: (dashboardStats?.bookCreatedStudents || 0) > 0 || false,
        size: 'medium',
        details: (
          <>
            <p>
              Let the creativity begin. Students use their logins to create stories, choose characters, write their
              text, and design illustrations. You can track each student's progress as they go in the teacher library.
            </p>
            <p>View the {makeLink(TeacherTimelineStepSourceURL.TeacherGuideStudentLogin, 'teacher guide.')}</p>
          </>
        ),
        taskIcon: '🚀',
        taskName: `Start with your class`,
        taskDescription: `Your class begins today. If you need help, call us at ${phoneNumber}.`,
        taskButtonText: 'Continue',
        taskClick: () => navigateTo('/classroom/library'),
      },
      {
        id: 'books-complete',
        title: 'Books Complete - Send Post-Session Flyers',
        icon: <DocumentArrowIcon />,
        completedIcon: <DocumentArrowIconDone />,
        isCompleted: timelineStepsValueMap.get(TimelineStepId.BOOKS_COMPLETE)?.isCompleted || false,
        size: 'huge',
        details: (
          <>
            <p>
              Once students have finished their books, click "Generate Flyers" to print Post-Session Flyers. These
              include a flipbook preview of each child’s story and a final order link for parents. Send them home and
              email parents using the prewritten message.
            </p>
            <p>
              Use the {makeLink(TeacherTimelineStepSourceURL.EmailTemplatePostSessionFlyers, 'email template')} and view
              the {makeLink(TeacherTimelineStepSourceURL.TeacherGuidePostFlyers, 'teacher guide.')}
            </p>
          </>
        ),
        taskIcon: '📖',
        taskName: `Share student books`,
        taskDescription: `Your students’ e-books are ready. Share them with families so they can celebrate.`,
        taskButtonText: 'Continue',
        taskClick: () => navigateTo('/classroom/library'),
      },
      {
        id: 'parent-deadline',
        title: 'Parent Order Deadline',
        icon: <HourglassIcon />,
        completedIcon: <HourglassIconDone />,
        isCompleted: activeClassroomProject?.parentOrderDeadline
          ? toStartOfLocalDay(new Date()).getTime() >=
            toStartOfLocalDay(convertTimestampToDateTimeString(activeClassroomProject?.parentOrderDeadline))?.getTime()
          : false,
        date: mapDateToLocalString(activeClassroomProject?.parentOrderDeadline || null),
        size: 'large',
        details: (
          <>
            <p>
              Once students have finished their books, click "Generate Flyers" to print Post-Session Flyers. These
              include a flipbook preview of each child’s story and a final order link for parents. Send them home and
              email parents using the prewritten message.
            </p>
            <p>Use the {makeLink(TeacherTimelineStepSourceURL.TeacherGuideOrderDeadline, 'teacher guide.')}</p>
          </>
        ),
        taskIcon: '⏳',
        taskName: `You’re all set until [${mapDateToLocalString(activeClassroomProject?.parentOrderDeadline || null)}]`,
        taskDescription: `Good job. Let's wait until ${mapDateToLocalString(activeClassroomProject?.parentOrderDeadline || null)}, so that all parents have time to place their orders.`,
        taskButtonText: 'Continue',
      },
      {
        id: 'ship-books',
        title: 'Send Class Shipment',
        icon: <BoxIcon />,
        completedIcon: <BoxIconDone />,
        // Event-first: the release itself marks SHIP_BOOKS complete on the server. The deadline
        // date stays as a fallback so long-past projects don't show an eternally open step.
        isCompleted:
          timelineStepsValueMap.get(TimelineStepId.SHIP_BOOKS)?.isCompleted ||
          (activeClassroomProject?.booksShippedDeadline
            ? toStartOfLocalDay(new Date()).getTime() >=
              toStartOfLocalDay(
                new Date(convertTimestampToDateTimeString(activeClassroomProject?.booksShippedDeadline)),
              )?.getTime()
            : false),
        date: mapDateToLocalString(activeClassroomProject?.booksShippedDeadline || null),
        size: 'medium',
        details: (
          <>
            <p>
              Your classroom gets one Class Shipment: every paid order is printed together and delivered to your
              school in a single box, arriving within 10 business days. Once families have ordered, go to the Library
              and click "Send Class Shipment" — the books wait for your approval. After it's sent, any new purchase
              ships directly to the buyer's own address, automatically.
            </p>
            <p>Use the {makeLink(TeacherTimelineStepSourceURL.TeacherGuideShipBooks, 'teacher guide.')}</p>
          </>
        ),
        taskIcon: '🚚',
        taskName: `Send your Class Shipment`,
        taskDescription: `Families have placed their orders — approve the Class Shipment so we can print and deliver to your school.`,
        taskButtonText: 'Continue',
        taskClick: () => navigateTo('/classroom/library'),
      },
      {
        id: 'celebrate',
        title: 'Celebrate!',
        icon: <ConfettiIcon />,
        completedIcon: <ConfettiIconDone />,
        isCompleted: activeClassroomProject?.celebrationCompletedDeadline
          ? toStartOfLocalDay(new Date()).getTime() >
            toStartOfLocalDay(
              new Date(convertTimestampToDateTimeString(activeClassroomProject?.celebrationCompletedDeadline)),
            )?.getTime()
          : false,
        date: mapDateToLocalString(activeClassroomProject?.celebrationCompletedDeadline || null),
        size: 'medium',
        details: (
          <>
            <p>
              The books are in. Time to celebrate your published authors. Throw a classroom party, plan a reading event,
              invite families, or let students host their own book signing. Don’t forget to take a class photo with the
              books to share with families and with us on social media using #buildmystoryclub
            </p>
            <p>Use the {makeLink(TeacherTimelineStepSourceURL.TeacherGuideCelebrate, 'teacher guide.')}</p>
          </>
        ),
        taskIcon: '🎉',
        taskName: `Celebrate!`,
        taskDescription: `Your class is complete. Celebrate your published authors!`,
        taskButtonText: 'Continue',
        taskClick: () => navigateTo('/classroom/library'),
      },
    ];

    if (startDate && new Date(startDate).getTime() < new Date().getTime()) {
      const prevoiusSteps = steps.slice(0, 4);
      if (prevoiusSteps.some((step) => !step.isCompleted)) {
        steps[4] = {
          ...steps[4],
          error: 'Complete all previous steps before setting the start date',
        };
      }
    }

    if (!startDate) {
      setNextTimelineStep({
        id: 'start-init-date',
        title: 'Start Date with Class',
        icon: <RocketTimelineIcon />,
        completedIcon: <RocketTimelineIconDone />,
        isCompleted: isStartDateCompleted || false,
        date: startDate || '-',
        size: 'small',
        details: (
          <>
            <p>
              This is your official project kickoff. On this date, students can either begin by brainstorming on
              printed writing sheets or dive straight into the platform if they are expanding on an idea.
            </p>
            <p>Download the {makeLink(TeacherTimelineStepSourceURL.WritingSheets, 'writing sheets')}</p>
          </>
        ),
        taskIcon: '📅',
        taskName: 'Pick your start date',
        taskDescription: 'Set when your class begins so we can guide you.',
        taskButtonText: 'Set Date',
        taskClick: () =>
          activeClassroomProject && dispatch(setShowPublishingTimelinePopup({ projectId: activeClassroomProject.id })),
      });
    } else {
      const nextStepIndex = steps.findIndex((step) => !step.isCompleted && step.taskName);
      const startDateTime =
        (startDate && toStartOfLocalDay(new Date(startDate)).getTime()) || toStartOfLocalDay(new Date()).getTime();
      if (
        startDateTime < toStartOfLocalDay(new Date(new Date()?.getTime() - 24 * 60 * 60 * 1000)).getTime() &&
        !steps[5]?.isCompleted
      ) {
        setNextTimelineStep({
          id: 'start-missed',
          title: 'Missed start date',
          icon: <RocketTimelineIcon />,
          completedIcon: <RocketTimelineIconDone />,
          isCompleted: false,
          date: startDate || '-',
          size: 'small',
          details: <></>,
          taskIcon: '📅',
          taskName: 'Update your start date',
          taskDescription: 'Looks like your start date has passed. Set a new date so we can guide you again.',
          taskButtonText: 'Set Date',
          taskClick: () =>
            activeClassroomProject &&
            dispatch(setShowPublishingTimelinePopup({ projectId: activeClassroomProject.id })),
        });
      } else if (nextStepIndex >= 4) {
        if (!startDate) {
          setNextTimelineStep(steps[4] || null);
        } else {
          console.log(steps[nextStepIndex]);
          setNextTimelineStep(steps[nextStepIndex] || null);
        }
      } else {
        setNextTimelineStep(steps[nextStepIndex] || null);
      }
    }

    return steps;
  };

  const handleProjectChange = (projectId: string) => {
    const project = activeClassroomProjects?.find((p) => p.id === projectId);
    if (project) {
      setSelectedProject(project);
      dispatch(setActiveClassroomProject({ project }));
      // Refresh students data for the new project
      setTimeout(() => fetchDashboardData(), 100);
    }
  };

  useEffect(() => {
    if (selectProjectById) {
      handleProjectChange(selectProjectById);
      dispatch(setSelectProjectById(null));
    }
  }, [selectProjectById]);

  const getProjectsList = () =>
    (activeClassroomProjects?.length || 0) > 0
      ? [
          ...(activeClassroomProjects?.map((item) => ({
            title: item.name || '',
            value: item.id || '',
            isDefaultProject: item.isDefaultProject,
          })) || []),
        ]
      : [];

  const timelineSteps = useMemo(
    () => calculateTimelineSteps(),
    [activeClassroomProject, students, dashboardStats, selectedProject, publishingKitResources, publishingKitResourcesLoading, publishingKitResourcesError], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const projectMetrics = useMemo(
    () => calculateMetrics(timelineSteps || []),
    [activeClassroomProject, students, dashboardStats, selectedProject],
  );

  if (classrooms && classrooms.length === 0 && !activeClassroom) {
    return (
      <div className="min-h-[calc(100vh-10rem)] flex w-full items-center justify-center font-sofiaSans">
        <div className="max-w-lg text-center px-6">
          <svg className="w-12 h-12 mx-auto mb-5 text-secondaryDescriptionTextColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <h2 className="text-2xl font-bold text-secondaryStoryBlueText mb-3">Welcome to your classroom</h2>
          <p className="text-sm text-secondaryStoryDescriptionGreyTextColor mb-6 leading-relaxed">
            You don't have any classrooms yet. To start creating books with your students, set up your first classroom — it only takes a couple of minutes.
          </p>
          <button
            onClick={() => navigateTo('/classroom/dashboard?addClassroom=true')}
            className="text-white bg-storyblueSecondLightText border border-storyblueSecondLightText rounded-2xl hover:shadow-md px-6 py-2 text-sm font-bold flex gap-2 items-center h-9 mx-auto"
          >
            Create your first classroom
          </button>
        </div>
      </div>
    );
  }

  if (loading && !teacherTourState?.show) {
    return (
      <div className="min-h-[calc(100vh-10rem)] flex w-full items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondaryStoryLightBlueText mx-auto"></div>
          <p className="mt-4 text-secondaryStoryBlackGrayText">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-10rem)] flex w-full font-sofiaSans">
      <div className="w-full max-w-7xl mx-auto px-6 py-6">
        <div className="flex flex-col md:flex-row gap-8 justify-between">
          <div className="flex flex-col flex-1 mb-4">
            <div className="flex w-full justify-between items-center mb-2 md:mb-4 gap-2">
              <h2 className="text-2xl font-bold text-secondaryStoryBlueText text-left">Project summary</h2>
              <div className="flex md:hidden items-center">
                <div className="hidden md:flex items-center flex-nowrap whitespace-nowrap font-bold pr-1.5">
                  Project -
                </div>
                <ProjectSelector
                  placeholder="Choose project"
                  selectedItem={selectedProject?.id || null}
                  onChange={(id) => handleProjectChange(id.value)}
                  options={getProjectsList()}
                  addNewItemTitle={'Add new project'}
                  addNewItemHandler={() => dispatch(setShowAddNewProject(true))}
                  showArchiveButton={true}
                ></ProjectSelector>
              </div>
            </div>
            <div className="teacher-tour-dashboard-project-summary-container flex flex-col md:flex-row justify-between items-start">
              <div className="flex w-full sm:w-auto border border-secondaryBorderColor rounded-xl shadow-sm overflow-hidden bg-white py-4 px-1">
                {projectMetrics.map((metric, index) => (
                  <div className={`${index > 1 ? 'hidden' : 'flex'} md:flex w-full sm:w-auto`}>
                    <div
                      key={metric.id}
                      className={`flex flex-col items-center justify-between min-w-full sm:min-w-44 md:min-w-0 px-4`}
                      onClick={metric.onClick}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex h-12">
                          {!metric?.status || metric?.status === 'ok' ? metric.icon : ErrorAlertRedIcon()}
                        </div>
                      </div>
                      <div className="flex flex-col items-center text-secondaryStoryOptionalLightBlueText">
                        <div className="flex items-center gap-0.5 mb-2">
                          <h3
                            className={`text-sm font-medium ${metric.type === 'editable' ? 'hover:underline cursor-pointer' : ''}`}
                          >
                            {metric.title}
                          </h3>
                          {metric.isEditable && (
                            <button
                              className="text-secondaryStoryOptionalLightBlueText hover:text-secondaryStoryBlueText"
                              onClick={metric.onClick}
                            >
                              <EditIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p
                          className={`
                          ${metric.value ? 'text-xl' : 'text-base'} font-bold hover:no-underline 
                          ${
                            !metric.status
                              ? 'text-secondaryStoryOptionalLightBlueText'
                              : metric.status === 'ok'
                                ? 'text-secondaryStorySuccessTextColor'
                                : 'text-secondaryStoryOrangeText'
                          }
                        `}
                        >
                          {metric.value || '-'}
                        </p>
                      </div>
                    </div>
                    {index !== projectMetrics?.length - 1 && (
                      <div className={`${index === 1 ? 'hidden' : 'flex'} md:flex h-full`}>
                        <div className="h-full border-l-2 border-secondaryBorderLightGreyColor"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex md:hidden mt-6 w-full sm:w-auto border border-secondaryBorderColor rounded-xl shadow-sm overflow-hidden bg-white py-4 px-1">
                {projectMetrics.map((metric, index) => (
                  <div className={`${index < 2 ? 'hidden' : 'flex'} md:hidden w-full sm:w-auto`}>
                    <div
                      key={metric.id}
                      className={`flex flex-col items-center justify-between min-w-full sm:min-w-44 md:min-w-0 px-4`}
                      onClick={metric.onClick}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex h-12">
                          {!metric?.status || metric?.status === 'ok' ? metric.icon : ErrorAlertRedIcon()}
                        </div>
                      </div>
                      <div className="flex flex-col items-center text-secondaryStoryOptionalLightBlueText">
                        <div className="flex items-center gap-0.5 mb-2">
                          <h3
                            className={`text-sm font-medium ${metric.type === 'editable' ? 'hover:underline cursor-pointer' : ''}`}
                          >
                            {metric.title}
                          </h3>
                          {metric.isEditable && (
                            <button
                              className="text-secondaryStoryOptionalLightBlueText hover:text-secondaryStoryBlueText"
                              onClick={metric.onClick}
                            >
                              <EditIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p
                          className={`
                          ${metric.value ? 'text-xl' : 'text-base'} font-bold hover:no-underline 
                          ${
                            !metric.status
                              ? 'text-secondaryStoryOptionalLightBlueText'
                              : metric.status === 'ok'
                                ? 'text-secondaryStorySuccessTextColor'
                                : 'text-secondaryStoryOrangeText'
                          }
                        `}
                        >
                          {metric.value || '-'}
                        </p>
                      </div>
                    </div>
                    {index !== projectMetrics?.length - 1 && (
                      <div className={`${index === 1 ? 'hidden' : 'flex'} md:flex h-full`}>
                        <div className="h-full border-l-2 border-secondaryBorderLightGreyColor"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/*<div className="hidden md:flex items-center">*/}
              {/*  <div className="hidden lg:flex items-center flex-nowrap whitespace-nowrap font-bold pr-1.5">*/}
              {/*    Assignment -*/}
              {/*  </div>*/}
              {/*  <ProjectSelector*/}
              {/*    placeholder="Choose Assignment"*/}
              {/*    selectedItem={selectedProject?.id || null}*/}
              {/*    onChange={(id) => handleProjectChange(id.value)}*/}
              {/*    options={getProjectsList()}*/}
              {/*    addNewItemTitle={'Add new Assignment'}*/}
              {/*    addNewItemHandler={() => setShowAddNewProject(true)}*/}
              {/*    showArchiveButton={true}*/}
              {/*  ></ProjectSelector>*/}
              {/*</div>*/}
            </div>
          </div>
          <div className="flex flex-col flex-1 mb-4 teacher-tour-dashboard-next-step-container">
            <div className="flex w-full justify-between mb-2">
              <h2 className="text-2xl font-bold text-secondaryStoryBlueText">Next step</h2>
              <div className="flex items-center">
                <div className="hidden lg:flex items-center flex-nowrap whitespace-nowrap font-bold pr-1.5">
                  Project -
                </div>
                <ProjectSelector
                  placeholder="Choose project"
                  selectedItem={selectedProject?.id || null}
                  onChange={(id) => handleProjectChange(id.value)}
                  options={getProjectsList()}
                  addNewItemTitle={'Add new project'}
                  addNewItemHandler={() => dispatch(setShowAddNewProject(true))}
                  showArchiveButton={true}
                ></ProjectSelector>
              </div>
            </div>
            <div className="flex flex-1">
              <div className="flex w-full justify-center border border-secondaryBorderColor rounded-xl shadow-sm overflow-hidden bg-white py-4 px-6">
                {nextTimelineStep ? (
                  <div className="w-full flex flex-col justify-between items-center font-sofiaSans">
                    <h2 className="text-secondaryStoryOptionalLightBlueText text-lg font-bold">
                      <span className="text-xl leading-none mr-1">{nextTimelineStep.taskIcon}</span>
                      <span>{nextTimelineStep.taskName}</span>
                    </h2>
                    <p className="w-full text-base text-secondaryStoryBlackText text-center">
                      {nextTimelineStep.taskDescription}
                    </p>
                    <div className="flex w-full justify-center items-center">
                      {nextTimelineStep.taskClick && (
                        <button
                          className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center`}
                          onClick={nextTimelineStep.taskClick}
                        >
                          <span>{nextTimelineStep.taskButtonText}</span>
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 p-2 rounded-full flex items-center justify-center mb-4 bg-secondaryStoryOptionalLightBlueText text-white">
                      {ConfettiIconDone()}
                    </div>
                    <h3 className="text-lg font-bold text-secondaryStoryBlueText mb-1">All steps completed!</h3>
                    <p className="text-sm text-secondaryStoryBlackGrayText">
                      You have completed all the steps in your project timeline. Great job!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Alert */}
        {/*{projectMetrics?.length > 0 && projectMetrics[0].status === 'error' && (*/}
        {/*  <div className="mb-8">*/}
        {/*    <div className="flex flex-col md:flex-row justify-between items-start">*/}
        {/*      <div className="w-full max-w-[26rem] sm:w-auto border border-secondaryBorderColor rounded-xl shadow-sm overflow-hidden bg-white p-4">*/}
        {/*        <span className="text-secondaryStoryOrangeText ml-1">Important!</span> Please update start date and*/}
        {/*        complete your prep in order to start making books with your class*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*)}*/}
        {/* Project timeline section */}
        <div className="mt-4">
          <div className="flex flex-col items-start mb-4 gap-1">
            <h2 className="text-2xl font-bold text-secondaryStoryBlueText">Project timeline</h2>
            <button
              className="teacher-tour-dashboard-tour-link-button flex items-center space-x-2 text-secondaryStoryOptionalLightBlueText text-base font-medium underline hover:underline"
              onClick={() => dispatch(setShowStartTourPopup(true))}
            >
              <span>Take a quick tour</span>
              {/*<InfoIcon*/}
              {/*  className={*/}
              {/*    'ml-3 mr-3 text-xs font-bold text-secondaryStoryOptionalLightBlueText px-[0.33rem] py-0.5 rounded-full border border-secondaryStoryOptionalLightBlueText cursor-pointer'*/}
              {/*  }*/}
              {/*/>*/}
            </button>
          </div>

          <div className="teacher-tour-dashboard-timeline-container border border-secondaryBorderColor rounded-xl shadow-sm overflow-hidden bg-white">
            <div className="overflow-x-auto p-6 py-8">
              <div className="flex flex-col pl-6 md:pl-0 md:flex-row justify-center items-start md:min-w-[1160px] lg:min-w-max relative">
                {timelineSteps.map((step, index) => (
                  <div
                    key={step.id}
                    style={{ '--step': `${100 / timelineSteps.length}%` } as React.CSSProperties}
                    className={clsx(
                      'flex items-center justify-start md:justify-between z-[1]',
                      // direction
                      !isOdd(index) ? 'flex-row md:flex-col' : 'flex-row md:flex-col-reverse',
                      // widths (base = full; md+ = %)
                      'w-full md:w-[var(--step)]',
                      'min-w-full md:min-w-[var(--step)]',
                      'max-w-full md:max-w-[var(--step)]',
                    )}
                  >
                    <div className="hidden md:flex h-28 py-4"></div>

                    {/* Timeline Step */}
                    <div className="flex flex-col items-center py-6 relative">
                      <div
                        className={`w-12 h-12 p-2.5 rounded-full flex items-center justify-center ${
                          // step.error
                          //   ? 'bg-secondaryStoryOrangeText text-white'
                          step.isCompleted
                            ? 'bg-secondaryStoryOptionalLightBlueText text-white'
                            : 'bg-white border-2 border-secondaryBorderBackgroundLightGreyColor'
                        }`}
                        style={{ zIndex: 2 }}
                      >
                        {
                          // step.error ? ErrorAlertWhiteIcon() :
                          step.isCompleted ? step.completedIcon : step.icon
                        }
                      </div>
                      <div
                        className="absolute left-0 right-0 m-auto h-[50%] w-0.5 rounded bg-secondaryBorderBackgroundLightGreyColor hidden md:flex justify-between"
                        style={{
                          zIndex: 1,
                          ...(!isOdd(index) ? { top: '50%' } : { bottom: '50%' }),
                        }}
                      ></div>
                      <div
                        className="absolute top-0 bottom-0 m-auto w-[100%] h-0.5 rounded bg-secondaryBorderBackgroundLightGreyColor flex md:hidden justify-between"
                        style={{
                          zIndex: 1,
                          left: '75%',
                        }}
                      ></div>
                    </div>

                    {/* Step Title and Details */}
                    <div className="w-full md:max-w-32 h-28 py-4 hidden md:flex items-center justify-center relative pl-14 md:pl-0">
                      <div
                        className={`flex flex-col items-start md:items-center text-center font-sofiaSans md:absolute
                        `}
                        style={{
                          width: `${100 + (step.size === 'huge' ? 40 : step.size === 'large' ? 30 : step.size === 'medium' ? 15 : 0)}%`,
                          maxWidth: `${100 + (step.size === 'huge' ? 40 : step.size === 'large' ? 30 : step.size === 'medium' ? 15 : 0)}%`,
                          minWidth: `${100 + (step.size === 'huge' ? 40 : step.size === 'large' ? 30 : step.size === 'medium' ? 15 : 0)}%`,
                          marginLeft: `-${step.size === 'huge' ? 20 : step.size === 'large' ? 15 : step.size === 'medium' ? 7.5 : 0}%`,
                          position: 'absolute',
                          left: 0,
                          top: '1rem',
                        }}
                      >
                        <h3
                          className={`text-base font-bold mb-2 leading-tight text-secondaryStoryOptionalLightBlueText text-start md:text-center`}
                        >
                          {step.title}
                        </h3>
                        {step.date && (
                          <p
                            className={`text-sm mb-2 italic ${step.error ? 'text-secondaryStoryOrangeText' : step.isCompleted ? 'text-secondaryStoryOptionalLightBlueText' : 'text-secondaryDescriptionTextColor'}`}
                          >
                            {step.error && (!step.date || step.date === '-') ? '-' : step.date}
                          </p>
                        )}
                        <button
                          className="flex items-center justify-center space-x-1 text-secondaryDescriptionTextColor hover:underline text-sm"
                          onClick={() => setShowTimelineDetails(step)}
                        >
                          <span>Details</span>
                          <InfoIcon className="ml-3 mr-3 text-[0.45rem] font-bold text-secondaryDescriptionTextColor px-[0.25rem] py-[0.115rem] rounded-full border border-secondaryDescriptionTextColor cursor-pointer" />
                        </button>
                      </div>
                    </div>
                    <div className="w-full md:w-auto md:max-w-32 h-28 py-4 flex md:hidden items-center justify-center relative pl-14 md:pl-0">
                      <div
                        className={`flex flex-col items-start md:items-center text-center font-sofiaSans md:absolute w-full min-w-full max-w-full
                        `}
                        style={{
                          left: 0,
                          top: '1rem',
                        }}
                      >
                        <h3
                          className={`text-base font-bold mb-2 leading-tight text-secondaryStoryOptionalLightBlueText text-start md:text-center`}
                        >
                          {step.title}
                        </h3>
                        {step.date && (
                          <p
                            className={`text-sm mb-2 italic ${step.isCompleted ? 'text-secondaryStoryOptionalLightBlueText' : 'text-secondaryDescriptionTextColor'}`}
                          >
                            {step.date}
                          </p>
                        )}
                        <button
                          className="flex items-center justify-center space-x-1 text-secondaryDescriptionTextColor hover:underline text-sm"
                          onClick={() => setShowTimelineDetails(step)}
                        >
                          <span>Details</span>
                          <InfoIcon className="ml-3 mr-3 text-[0.45rem] font-bold text-secondaryDescriptionTextColor px-[0.25rem] py-[0.115rem] rounded-full border border-secondaryDescriptionTextColor cursor-pointer" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Horizontal Line */}
                <div
                  className="absolute top-0 left-0 bottom-0 m-auto w-full h-0.5 rounded bg-secondaryBorderBackgroundLightGreyColor hidden md:flex justify-between"
                  style={{ zIndex: 0 }}
                >
                  <div className="w-3 h-3 rounded-full bg-secondaryBorderBackgroundLightGreyColor -mt-[0.3rem] -ml-1"></div>
                  <div className="w-3 h-3 rounded-full bg-secondaryBorderBackgroundLightGreyColor -mt-[0.3rem] -mr-1"></div>
                </div>
                <div
                  className="absolute top-0 bottom-0 left-12 m-auto h-full w-0.5 rounded bg-secondaryBorderBackgroundLightGreyColor flex-col md:hidden justify-between"
                  style={{ zIndex: 0 }}
                >
                  <div className="w-3 h-3 rounded-full bg-secondaryBorderBackgroundLightGreyColor -mt-[0.3rem] -ml-[0.3rem] absolute"></div>
                  <div className="w-3 h-3 rounded-full bg-secondaryBorderBackgroundLightGreyColor -mb-[0.3rem] -ml-[0.3rem] absolute bottom-0"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showTimelineDetails && (
        <Modal
          title={showTimelineDetails.title}
          className={
            'global-absolute-popup-style font-sofiaSans content-left secondary-small dashboard-timeline-details'
          }
          mainContainerClassName={'sm:max-w-[500px]'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={true}
          showCloseButtonFunction={() => setShowTimelineDetails(null)}
        >
          <div className="text-storyblackText font-sofiaSans text-sm">{timelineSteps.find((s) => s.id === showTimelineDetails.id)?.details ?? showTimelineDetails.details}</div>
        </Modal>
      )}
    </div>
  );
};

export default TeacherClassroomDashboard;
